import React from 'react';
import { MdClose as CloseIcon } from 'react-icons/md';
import styled, { css } from 'styled-components';

import { Button, ButtonSizeEnum, ButtonVariantEnum } from '../../atoms/button';
import { Loader } from '../../atoms/loader';
import { LoaderColorVariantEnum, LoaderSizeEnum } from '../../atoms/loader/loader.types';
import { ColorVariantEnum, MarginSizeEnum, Text } from '../../atoms/text';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { theme } from '../../theme';
import { ModalPortal } from './modal-portal';
import {
	IModal,
	IModalContainer,
	IModalContentContainer,
	IModalHeader,
	ModalSizeEnum,
	ModalTypeEnum,
} from './modal.types';

const modalStylesPerSize = {
	[ModalSizeEnum.DEFAULT]: css`
		width: 464px;
	`,
	[ModalSizeEnum.FULL_WIDTH]: css`
		height: calc(100% - 24px);
		width: calc(100% - 24px);
	`,
	[ModalSizeEnum.MEDIUM]: css`
		width: 680px;
	`,
	[ModalSizeEnum.LARGE]: css`
		width: 960px;
	`,
	[ModalSizeEnum.AUTO]: css`
		width: auto;
	`,
};

const TOPBAR_HEIGHT = '56px';
const BOTTOMBAR_HEIGHT = '120px';

const getGridRows = (withTopbar: boolean, noButtons: boolean) => {
	if (withTopbar && noButtons) {
		return 'auto';
	}
	if (withTopbar) {
		return `1fr ${BOTTOMBAR_HEIGHT}`;
	}
	if (noButtons) {
		return `${TOPBAR_HEIGHT} 1fr`;
	}
	return `${TOPBAR_HEIGHT} 1fr ${BOTTOMBAR_HEIGHT}`;
};

const getGrid = (gridRows: string) => `
		max-height: 95vh;
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: ${gridRows}
		`;

const Container = styled.div`
	position: fixed;
	height: 100%;
	width: 100%;
	z-index: 251;
	background: rgba(0, 0, 0, 0.6);
`;

const ModalContainer = styled.div<IModalContainer>`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 8px;
	background-color: ${(props) => props.backgroundColor};
	min-height: ${(props) => (props.isLoading ? '300px' : '')};
	box-shadow: 0 32px 40px -2px rgba(0, 43, 73, 0.12), 0 0 1px 0 rgba(0, 43, 73, 0.06);
	${(props) => modalStylesPerSize[props.size || ModalSizeEnum.DEFAULT]};
	padding: ${(props) => (props.isCustomType ? 0 : '32px 32px 0 32px')};
`;

const ContentContainer = styled.div<IModalContentContainer>`
	height: 100%;
	width: 100%;
	margin-top: ${(props) => (props.withMargin ? '80px' : 0)};
	${(props) => (props.isCustomType ? '' : getGrid(getGridRows(props.withTopbar, props.noButtons)))};
`;

const ModalHeader = styled.div<IModalHeader>`
	background-color: ${(props) =>
		props.withBackground ? theme.colors.modal.header.background.blue : theme.colors.modal.header.background.default};
	color: ${(props) =>
		props.withBackground ? theme.colors.modal.header.background.blue : theme.colors.modal.header.background.default};
	position: absolute;
	top: 0;
	padding-left: 24px;
	width: 100%;
	height: 56px;
	display: flex;
	align-items: center;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
`;

const LoaderWrapper = styled.div`
	padding-top: 68px;
	padding-bottom: 100px;
`;

const ButtonContainer = styled.div`
	display: grid;
	grid-auto-flow: column;
	justify-content: flex-end;
	align-items: center;
	column-gap: 16px;
	margin-top: 40px;
	padding-bottom: 32px;
`;

const StyledCloseIcon = styled(CloseIcon)`
	position: absolute;
	cursor: pointer;
	top: 16px;
	right: 16px;
	transition: color 300ms ease;
	color: ${theme.colors.modal.icons.gray};
	&:hover {
		color: ${theme.colors.modal.icons.hover};
	}
`;

const StyledCloseIconWithTopBar = styled(StyledCloseIcon)`
	&:hover {
		color: ${theme.colors.modal.icons.hoverWithTopBar};
	}
`;

export const Modal: React.FunctionComponent<IModal> = ({
	title,
	isOpen,
	backgroundColor = theme.colors.modal.background.default,
	closable = true,
	isLoading = false,
	withTopBar = false,
	closeOnClickOutside = false,
	noButtons = false,
	modalType,
	modalSize,
	content,
	customSecondaryButton,
	primaryButtonDisabled,
	primaryButtonText,
	primaryButtonTestId,
	secondaryButtonText,
	secondaryButtonTestId,
	onPrimaryButtonClick,
	onSecondaryButtonClick,
	onCloseIconClick,
	className,
}) => {
	const modalRef = React.useRef();

	const onClickOutside = () => {
		if (closeOnClickOutside) {
			if (onSecondaryButtonClick) {
				return onSecondaryButtonClick();
			}
			if (onCloseIconClick) {
				return onCloseIconClick();
			}
			return null;
		}
	};

	useOnClickOutside(modalRef, () => onClickOutside && onClickOutside());
	const isErrorType: boolean = Boolean(modalType && modalType === ModalTypeEnum.ERROR);
	const isConfirmType: boolean = Boolean(modalType && modalType === ModalTypeEnum.CONFIRM);
	const isCustomType: boolean = Boolean(modalType && modalType === ModalTypeEnum.CUSTOM);
	const withButtons = (): boolean => {
		if (isCustomType) {
			return false;
		}
		if (noButtons) {
			return false;
		}
		return true;
	};

	if (isOpen) {
		return (
			<ModalPortal>
				<Container className={className}>
					<ModalContainer
						size={modalSize}
						isLoading={isLoading}
						isCustomType={isCustomType}
						backgroundColor={backgroundColor}
						// tslint:disable-next-line: no-any
						ref={modalRef as any}
						data-testid="modal-container"
					>
						{withTopBar && (
							<ModalHeader withBackground={withTopBar}>
								<Text.SubTitle colorVariant={ColorVariantEnum.WHITE} margin={MarginSizeEnum.NONE}>
									{title}
								</Text.SubTitle>
								{closable && (
									<StyledCloseIconWithTopBar
										size={24}
										onClick={onCloseIconClick || onSecondaryButtonClick}
										data-testid="close"
									/>
								)}
							</ModalHeader>
						)}
						{isLoading && (
							<LoaderWrapper>
								<Loader colorVariant={LoaderColorVariantEnum.DARK} size={LoaderSizeEnum.LARGE} />
							</LoaderWrapper>
						)}
						{!isLoading && (
							<ContentContainer
								withMargin={withTopBar && !noButtons}
								isCustomType={isCustomType}
								noButtons={noButtons}
								withTopbar={withTopBar}
							>
								{!withTopBar && closable && !isCustomType && (
									<StyledCloseIcon size={24} onClick={onCloseIconClick || onSecondaryButtonClick} data-testid="close" />
								)}
								{!withTopBar && !isCustomType && (
									<Text.Title colorVariant={isErrorType ? ColorVariantEnum.ERROR : ColorVariantEnum.DARK}>
										{title}
									</Text.Title>
								)}
								{content}
								{withButtons() && (
									<ButtonContainer>
										{!isConfirmType && !customSecondaryButton && (
											<Button
												variant={ButtonVariantEnum.SECONDARY_DARK}
												size={ButtonSizeEnum.LARGE}
												onClick={onSecondaryButtonClick}
												data-testid={secondaryButtonTestId}
											>
												{secondaryButtonText || 'Cancel'}
											</Button>
										)}
										{!isConfirmType && customSecondaryButton}
										<Button
											variant={
												primaryButtonDisabled
													? ButtonVariantEnum.DISABLED
													: isErrorType
													? ButtonVariantEnum.ERROR
													: ButtonVariantEnum.PRIMARY
											}
											size={ButtonSizeEnum.LARGE}
											disabled={primaryButtonDisabled}
											onClick={onPrimaryButtonClick}
											data-testid={primaryButtonTestId}
										>
											{primaryButtonText || 'Save'}
										</Button>
									</ButtonContainer>
								)}
							</ContentContainer>
						)}
					</ModalContainer>
				</Container>
			</ModalPortal>
		);
	}
	return null;
};
